import { useState } from 'react';
import { Card, CardBody, Button } from '@components/ui';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@components/ui';
import { CheckCircleIcon, EyeIcon } from '@heroicons/react/24/outline';
import { formatNumber, formatPriceDisplay } from '@/utils/format';
import { OTI_CODES } from '@features/products/constants/taxCategories';
import { InvoicePreviewModal } from './InvoicePreviewModal';
import type { InvoiceWizardData } from '../../types/invoiceWizard.types';
import type { CompanyConsumption, AvailablePlan } from '@features/subscriptions/types/subscription.types';

interface Step5ReviewProps {
  wizardData: InvoiceWizardData;
  onSubmit: () => void;
  onPrevious: () => void;
  isSubmitting?: boolean;
  // Plan information (optional)
  currentPlan?: AvailablePlan;
  consumption?: CompanyConsumption;
  remainingInvoices?: number | null;
}

const paymentMethodLabels: Record<string, string> = {
  '01': 'Crédito',
  '02': 'Efectivo',
  '03': 'Tarjeta Crédito',
  '04': 'Tarjeta Débito',
  '05': 'Tarjeta Fidelización',
  '06': 'Vale',
  '07': 'Tarjeta de Regalo',
  '08': 'Transf./Depósito a cta. Bancaria',
  '09': 'Cheque',
  '10': 'Punto de Pago',
  '99': 'Otro',
};

const documentTypeLabels: Record<string, string> = {
  '01': 'Factura de Operación Interna',
  '02': 'Factura de Importación',
  '03': 'Factura de Exportación',
  '04': 'Nota de Crédito',
  '05': 'Nota de Débito',
  '06': 'Nota de Crédito Genérica',
  '07': 'Nota de Débito Genérica',
  '08': 'Factura de Zona Franca',
  '09': 'Reembolso',
  '10': 'Factura de operación extranjera',
};

// Formatear fecha desde string YYYY-MM-DD sin conversión de zona horaria
const formatDate = (dateString: string): string => {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-');
  return `${day}/${month}/${year}`;
};

export function Step5Review({
  wizardData,
  onSubmit,
  onPrevious,
  isSubmitting,
  currentPlan,
  consumption,
  remainingInvoices
}: Step5ReviewProps) {
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

  // Acarreo y Seguro informados a nivel de ítem (C204/C205). Se muestran aparte del
  // "Acarreo Global"/"Seguro Global" (a nivel de factura) para que el desglose del
  // Resumen de Totales reconcilie visualmente con el Total a Pagar.
  const itemLevelShipping = wizardData.items.reduce((sum, item) => sum + (item.shippingAmount || 0), 0);
  const itemLevelInsurance = wizardData.items.reduce((sum, item) => sum + (item.insuranceAmount || 0), 0);

  return (
    <div className="space-y-6" data-testid="invoice-wizard-step5-container">
      <div>
        <h2 className="text-2xl font-bold text-secondary-900 dark:text-white">Revisar Factura</h2>
        <p className="mt-2 text-secondary-600 dark:text-secondary-400">
          Verifica todos los detalles antes de emitir la factura
        </p>
      </div>

      {/* Plan Information Banner */}
      {currentPlan && consumption && (
        <div className="bg-primary-50 dark:bg-primary-900/20 p-4 rounded-lg border border-primary-200 dark:border-primary-800">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="text-sm font-medium text-primary-900 dark:text-primary-100 mb-1">
                Plan Actual: {currentPlan.name}
              </h3>
              <p className="text-xs text-primary-700 dark:text-primary-300">
                {consumption.MonthlyInvoiceQty} de {currentPlan.limits.facturas_mes === -1 ? 'ilimitadas' : currentPlan.limits.facturas_mes} facturas mensuales utilizadas
              </p>
            </div>
            {remainingInvoices !== null && remainingInvoices !== -1 && (
              <div className="text-right">
                <p className="text-lg font-semibold text-primary-900 dark:text-primary-100">
                  {remainingInvoices}
                </p>
                <p className="text-xs text-primary-700 dark:text-primary-300">
                  restantes
                </p>
              </div>
            )}
            {remainingInvoices === -1 && (
              <div className="text-right">
                <p className="text-lg font-semibold text-primary-900 dark:text-primary-100">
                  ∞
                </p>
                <p className="text-xs text-primary-700 dark:text-primary-300">
                  ilimitadas
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Header with Issuer/Branch and Customer side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left side: Issuer and Branch Office */}
        <div className="space-y-4">
          {/* Issuer Information */}
          <Card>
            <CardBody>
              <h3 className="text-lg font-semibold text-secondary-900 dark:text-white mb-4 border-b border-secondary-200 dark:border-secondary-700 pb-2">
                Información del Emisor
              </h3>
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-secondary-600 dark:text-secondary-400">Razón Social</p>
                  <p className="font-semibold text-secondary-900 dark:text-white">{wizardData.issuerName}</p>
                </div>
                <div>
                  <p className="text-xs text-secondary-600 dark:text-secondary-400">RUC</p>
                  <p className="font-medium text-secondary-900 dark:text-white">
                    {wizardData.issuerCode}-{wizardData.issuerDv}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-secondary-600 dark:text-secondary-400">Email</p>
                    <p className="text-sm font-medium text-secondary-900 dark:text-white">{wizardData.issuerEmail || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-secondary-600 dark:text-secondary-400">Teléfono</p>
                    <p className="text-sm font-medium text-secondary-900 dark:text-white">{wizardData.issuerPhone || '-'}</p>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Branch Office Information */}
          <Card>
            <CardBody>
              <h3 className="text-lg font-semibold text-secondary-900 dark:text-white mb-4 border-b border-secondary-200 dark:border-secondary-700 pb-2">
                Información de la Sucursal
              </h3>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-secondary-600 dark:text-secondary-400">Sucursal</p>
                    <p className="font-medium text-secondary-900 dark:text-white">{wizardData.branchOfficeName || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-secondary-600 dark:text-secondary-400">Punto de Facturación</p>
                    <p className="font-medium text-secondary-900 dark:text-white">{wizardData.billingPointCode || '-'}</p>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-secondary-600 dark:text-secondary-400">Dirección</p>
                  <p className="text-sm font-medium text-secondary-900 dark:text-white">
                    {wizardData.branchOfficeAddress}
                    {wizardData.branchOfficeTownship && `, ${wizardData.branchOfficeTownship}`}
                    {wizardData.branchOfficeDistrict && `, ${wizardData.branchOfficeDistrict}`}
                    {wizardData.branchOfficeProvince && `, ${wizardData.branchOfficeProvince}`}
                  </p>
                  {wizardData.branchOfficeFiscalCode && (
                    <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-1">
                      Código Fiscal: {wizardData.branchOfficeFiscalCode}
                    </p>
                  )}
                  {wizardData.branchOfficeLatitude && wizardData.branchOfficeLongitude && (
                    <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-1">
                      Coordenadas: {wizardData.branchOfficeLatitude}, {wizardData.branchOfficeLongitude}
                    </p>
                  )}
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Right side: Customer Information */}
        <Card>
          <CardBody>
            <h3 className="text-lg font-semibold text-secondary-900 dark:text-white mb-4 border-b border-secondary-200 dark:border-secondary-700 pb-2">
              Información del Cliente
            </h3>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-secondary-600 dark:text-secondary-400">Nombre</p>
                <p className="font-semibold text-secondary-900 dark:text-white">{wizardData.customerName}</p>
              </div>
              <div>
                <p className="text-xs text-secondary-600 dark:text-secondary-400">RUC</p>
                <p className="font-medium text-secondary-900 dark:text-white">
                  {wizardData.customerRuc}-{wizardData.customerDv}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-secondary-600 dark:text-secondary-400">Email</p>
                  <p className="text-sm font-medium text-secondary-900 dark:text-white">{wizardData.customerEmail}</p>
                </div>
                <div>
                  <p className="text-xs text-secondary-600 dark:text-secondary-400">Teléfono</p>
                  <p className="text-sm font-medium text-secondary-900 dark:text-white">{wizardData.customerPhone || '-'}</p>
                </div>
              </div>
              <div>
                <p className="text-xs text-secondary-600 dark:text-secondary-400">Destinatarios de notificación</p>
                {wizardData.notificationReceivers && wizardData.notificationReceivers.filter((r) => r.Email?.trim()).length > 0 ? (
                  <ul className="text-sm font-medium text-secondary-900 dark:text-white list-disc list-inside">
                    {wizardData.notificationReceivers
                      .filter((r) => r.Email?.trim())
                      .map((r, index) => (
                        <li key={index}>{r.Email}{r.Name ? ` (${r.Name})` : ''}</li>
                      ))}
                  </ul>
                ) : (
                  <p className="text-sm font-medium text-secondary-900 dark:text-white">-</p>
                )}
              </div>
              <div>
                <p className="text-xs text-secondary-600 dark:text-secondary-400">Dirección Fiscal</p>
                <p className="text-sm font-medium text-secondary-900 dark:text-white">
                  {wizardData.customerAddress}
                  {wizardData.customerTownship && `, ${wizardData.customerTownship}`}
                  {wizardData.customerDistrict && `, ${wizardData.customerDistrict}`}
                  {wizardData.customerProvince && `, ${wizardData.customerProvince}`}
                  {wizardData.customerCountry && `, ${wizardData.customerCountry}`}
                </p>
                {wizardData.customerAddressFiscalCode && (
                  <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-1">
                    Código Fiscal: {wizardData.customerAddressFiscalCode}
                  </p>
                )}
              </div>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Invoice Details */}
      <Card>
        <CardBody>
          <h3 className="text-lg font-semibold text-secondary-900 dark:text-white mb-4">
            Detalles de la Factura
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">Fecha de Emisión</p>
              <p className="font-medium text-secondary-900 dark:text-white">
                {formatDate(wizardData.invoiceDate)}
              </p>
            </div>
            <div>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">Fecha de Vencimiento</p>
              <p className="font-medium text-secondary-900 dark:text-white">
                {formatDate(wizardData.dueDate)}
              </p>
            </div>
            <div>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">Tipo de Documento</p>
              <p className="font-medium text-secondary-900 dark:text-white">
                {documentTypeLabels[wizardData.documentType] || wizardData.documentType}
              </p>
            </div>
            <div>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">Moneda</p>
              <p className="font-medium text-secondary-900 dark:text-white">{wizardData.currency}</p>
            </div>
            {wizardData.reference && (
              <div>
                <p className="text-sm text-secondary-600 dark:text-secondary-400">Referencia</p>
                <p className="font-medium text-secondary-900 dark:text-white">{wizardData.reference}</p>
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      {/* Items */}
      <Card>
        <CardBody className="p-0">
          <div className="p-6 pb-0">
            <h3 className="text-lg font-semibold text-secondary-900 dark:text-white mb-4">
              Items de la Factura
            </h3>
          </div>
          <Table data-testid="invoice-wizard-step5-items-table">
            <TableHeader>
              <TableRow>
                <TableHead>Descripción</TableHead>
                <TableHead className="text-right">Cant.</TableHead>
                <TableHead className="text-right">Precio</TableHead>
                <TableHead className="text-right">Desc.</TableHead>
                <TableHead className="text-right">ITBMS</TableHead>
                <TableHead className="text-right">ISC</TableHead>
                <TableHead className="text-right">OTI</TableHead>
                <TableHead className="text-right">Acarreo</TableHead>
                <TableHead className="text-right">Seguro</TableHead>
                <TableHead className="text-right">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {wizardData.items.map((item, index) => (
                <TableRow key={item.id} data-testid={`invoice-wizard-step5-item-row-${index}`}>
                  <TableCell>
                    <div>
                      <div className="font-medium">{item.productName}</div>
                      <div className="text-sm text-secondary-500">{item.productCode}</div>
                      {item.description && (
                        <div className="text-xs text-secondary-400 mt-1">{item.description}</div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">{formatNumber(item.quantity, 2)}</TableCell>
                  <TableCell className="text-right">${formatPriceDisplay(item.unitPrice)}</TableCell>
                  <TableCell className="text-right">
                    {item.unitDiscount > 0 ? `$${formatPriceDisplay(item.unitDiscount)}` : '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="text-xs text-secondary-500">{item.taxRate}%</div>
                    <div>${formatPriceDisplay(item.taxAmount)}</div>
                  </TableCell>
                  <TableCell className="text-right">
                    {item.iscRate > 0 ? (
                      <>
                        <div className="text-xs text-secondary-500">{item.iscRate}%</div>
                        <div>${formatPriceDisplay(item.iscAmount)}</div>
                      </>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {item.gOTIItem && item.gOTIItem.length > 0 ? (
                      <div className="min-w-[140px]">
                        {item.gOTIItem.map((oti, otiIdx) => {
                          const otiCode = OTI_CODES.find(c => c.code === oti.dCodOTI);
                          return (
                            <div key={otiIdx} className="text-xs text-secondary-500 mb-1">
                              <span className="font-medium">{otiCode?.code}</span>: ${formatNumber(parseFloat(oti.dValOTI))}
                              <div className="text-[11px] text-secondary-400 truncate">
                                {otiCode?.description}
                              </div>
                            </div>
                          );
                        })}
                        <div className="font-semibold mt-2 pt-1 border-t border-secondary-200 dark:border-secondary-700">
                          ${formatNumber(item.otiAmount)}
                        </div>
                      </div>
                    ) : item.otiAmount > 0 ? (
                      `$${formatNumber(item.otiAmount)}`
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {item.shippingAmount && item.shippingAmount > 0 ? `$${formatNumber(item.shippingAmount)}` : '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    {item.insuranceAmount && item.insuranceAmount > 0 ? `$${formatNumber(item.insuranceAmount)}` : '-'}
                  </TableCell>
                  <TableCell className="text-right font-semibold">${formatPriceDisplay(item.total)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardBody>
      </Card>

      {/* Totals */}
      <Card>
        <CardBody>
          <h3 className="text-lg font-semibold text-secondary-900 dark:text-white mb-4">
            Resumen de Totales
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-secondary-600 dark:text-secondary-400">Subtotal:</span>
              <span className="font-medium text-secondary-900 dark:text-white">
                ${formatNumber(wizardData.subtotal)}
              </span>
            </div>
            {wizardData.totalDiscount > 0 && (
              <div className="flex justify-between">
                <span className="text-secondary-600 dark:text-secondary-400">Descuentos (Items):</span>
                <span className="font-medium text-danger-600">
                  -${formatNumber(wizardData.totalDiscount)}
                </span>
              </div>
            )}
            {wizardData.globalDiscounts && wizardData.globalDiscounts.length > 0 && (
              <>
                {wizardData.globalDiscounts.map((discount, index) => (
                  <div key={index} className="flex justify-between">
                    <span className="text-secondary-600 dark:text-secondary-400 text-sm">
                      Desc. Global: {discount.description}
                    </span>
                    <span className="font-medium text-danger-600 text-sm">
                      -${formatNumber(discount.amount)}
                    </span>
                  </div>
                ))}
              </>
            )}
            <div className="flex justify-between">
              <span className="text-secondary-600 dark:text-secondary-400">ITBMS (Impuestos):</span>
              <span className="font-medium text-secondary-900 dark:text-white">
                ${formatNumber(wizardData.totalTax)}
              </span>
            </div>
            {wizardData.totalIsc > 0 && (
              <div className="flex justify-between">
                <span className="text-secondary-600 dark:text-secondary-400">ISC (Impuesto Selectivo):</span>
                <span className="font-medium text-secondary-900 dark:text-white">
                  ${formatNumber(wizardData.totalIsc)}
                </span>
              </div>
            )}
            {wizardData.gOTITotal && wizardData.gOTITotal.length > 0 && (
              <div className="border-l-4 border-primary-400 pl-3 py-2 bg-primary-50 dark:bg-primary-900/20 rounded">
                <div className="font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                  OTI (Otros Tributos e Impuestos):
                </div>
                {wizardData.gOTITotal.map((oti, index) => {
                  const otiCode = OTI_CODES.find(c => c.code === oti.dCodOTITotal);
                  return (
                    <div key={index} className="flex justify-between items-start mb-1 text-sm">
                      <div className="flex-1">
                        <span className="font-medium text-secondary-600 dark:text-secondary-400">
                          {oti.dCodOTITotal}
                        </span>
                        <span className="text-xs text-secondary-500 dark:text-secondary-400 ml-2">
                          {otiCode?.description || 'Otro'}
                        </span>
                      </div>
                      <span className="font-medium text-secondary-900 dark:text-white ml-3">
                        ${formatNumber(parseFloat(oti.dValOTITotal))}
                      </span>
                    </div>
                  );
                })}
                <div className="flex justify-between pt-2 mt-2 border-t border-primary-200 dark:border-primary-800">
                  <span className="font-semibold text-secondary-700 dark:text-secondary-300">Total OTI:</span>
                  <span className="font-semibold text-secondary-900 dark:text-white">
                    ${formatNumber(wizardData.totalOti)}
                  </span>
                </div>
              </div>
            )}
            {itemLevelShipping > 0 && (
              <div className="flex justify-between">
                <span className="text-secondary-600 dark:text-secondary-400">Acarreo (Items):</span>
                <span className="font-medium text-secondary-900 dark:text-white">
                  ${formatNumber(itemLevelShipping)}
                </span>
              </div>
            )}
            {itemLevelInsurance > 0 && (
              <div className="flex justify-between">
                <span className="text-secondary-600 dark:text-secondary-400">Seguro (Items):</span>
                <span className="font-medium text-secondary-900 dark:text-white">
                  ${formatNumber(itemLevelInsurance)}
                </span>
              </div>
            )}
            {wizardData.shippingCost > 0 && (
              <div className="flex justify-between">
                <span className="text-secondary-600 dark:text-secondary-400">Acarreo Global:</span>
                <span className="font-medium text-secondary-900 dark:text-white">
                  ${formatNumber(wizardData.shippingCost)}
                </span>
              </div>
            )}
            {wizardData.insuranceCost > 0 && (
              <div className="flex justify-between">
                <span className="text-secondary-600 dark:text-secondary-400">Seguro Global:</span>
                <span className="font-medium text-secondary-900 dark:text-white">
                  ${formatNumber(wizardData.insuranceCost)}
                </span>
              </div>
            )}
            <div className="flex justify-between text-xl font-bold pt-3 border-t border-secondary-200 dark:border-secondary-700">
              <span>Total a Pagar:</span>
              <span className="text-primary-600">${formatNumber(wizardData.grandTotal)} {wizardData.currency}</span>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Payment Methods */}
      <Card>
        <CardBody>
          <h3 className="text-lg font-semibold text-secondary-900 dark:text-white mb-4">
            Formas de Pago
          </h3>
          {wizardData.payments && wizardData.payments.length > 0 ? (
            <div className="space-y-3">
              {wizardData.payments.map((payment, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-secondary-50 dark:bg-secondary-800 rounded-lg"
                >
                  <div className="flex-1">
                    <p className="font-medium text-secondary-900 dark:text-white">
                      {paymentMethodLabels[payment.method] || payment.method}
                    </p>
                    {payment.description && (
                      <p className="text-sm text-secondary-500 dark:text-secondary-400">
                        {payment.description}
                      </p>
                    )}
                  </div>
                  <div className="font-bold text-primary-600 text-lg">
                    ${formatNumber(payment.amount)}
                  </div>
                </div>
              ))}
              <div className="flex justify-between items-center pt-3 border-t border-secondary-200 dark:border-secondary-700">
                <span className="font-bold text-secondary-900 dark:text-white">Total Pagado:</span>
                <span className="font-bold text-success-600 text-xl">
                  ${formatNumber(wizardData.payments.reduce((sum, p) => sum + p.amount, 0))}
                </span>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-secondary-600 dark:text-secondary-400">Método de Pago</p>
                <p className="font-medium text-secondary-900 dark:text-white">
                  {paymentMethodLabels[wizardData.paymentMethod] || wizardData.paymentMethod}
                </p>
              </div>
              <div>
                <p className="text-sm text-secondary-600 dark:text-secondary-400">Monto Pagado</p>
                <p className="font-medium text-secondary-900 dark:text-white">
                  ${formatNumber(wizardData.amountPaid)}
                </p>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Notes */}
      {wizardData.notes && (
        <Card>
          <CardBody>
            <h3 className="text-lg font-semibold text-secondary-900 dark:text-white mb-4">
              Notas
            </h3>
            <div>
              <p className="text-sm font-medium text-secondary-700 dark:text-secondary-300">Notas para el Cliente</p>
              <p className="text-sm text-secondary-600 dark:text-secondary-400 mt-1">{wizardData.notes}</p>
            </div>
          </CardBody>
        </Card>
      )}

      <div className="flex justify-between">
        <Button variant="outline" onClick={onPrevious} disabled={isSubmitting} data-testid="invoice-wizard-step5-previous-button">
          Anterior
        </Button>
        <div className="flex gap-3">
          <Button
            variant="outline"
            leftIcon={<EyeIcon className="w-5 h-5" />}
            onClick={() => setIsPreviewModalOpen(true)}
            disabled={isSubmitting}
            data-testid="invoice-wizard-step5-preview-button"
          >
            Vista Previa
          </Button>
          <Button
            variant="success"
            leftIcon={<CheckCircleIcon className="w-5 h-5" />}
            onClick={onSubmit}
            isLoading={isSubmitting}
            data-testid="invoice-wizard-step5-submit-button"
          >
            Emitir Factura
          </Button>
        </div>
      </div>

      {/* Invoice Preview Modal */}
      <InvoicePreviewModal
        isOpen={isPreviewModalOpen}
        onClose={() => setIsPreviewModalOpen(false)}
        wizardData={wizardData}
      />
    </div>
  );
}
